import { useEffect, useState } from "react";
import { Table2, Download, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const TABS = [
  { key: "rfq", label: "RFQ lines", endpoint: "/analytics/rfq-lines" },
  { key: "estimate", label: "Estimate lines", endpoint: "/analytics/estimate-lines" },
  { key: "so", label: "SO lines", endpoint: "/analytics/so-lines" },
  { key: "invoice", label: "Invoice lines", endpoint: "/analytics/invoice-lines" },
];

export default function LineAnalytics() {
  const { activeWorkspaceId } = useAuth();
  const [tab, setTab] = useState("rfq");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const cur = TABS.find((t) => t.key === tab);
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}${cur.endpoint}`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("You don't have permission to see this data");
      else toast.error("Failed to load");
      setRows([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId, tab]);

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return Object.values(r).some((v) => String(v || "").toLowerCase().includes(s));
  });

  const columns = filtered.length > 0
    ? Object.keys(filtered[0]).filter((k) => !k.startsWith("_"))
    : [];

  const exportCsv = () => {
    if (!filtered.length) return;
    const header = columns.join(",");
    const body = filtered.map((r) =>
      columns.map((c) => {
        const v = r[c];
        if (v === null || v === undefined) return "";
        const s = String(v).replace(/"/g, '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
      }).join(","),
    ).join("\n");
    const blob = new Blob([header + "\n" + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${tab}-lines-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-5" data-testid="line-analytics-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase text-muted-foreground tracking-wide flex items-center gap-1.5">
            <Table2 className="w-3 h-3" /> Estimates & Contracts
          </div>
          <h1 className="text-3xl font-bold mt-0.5 tracking-tight">Line-item Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Flat table of every line across every RFQ / estimate / sales order / invoice — search, sort, export.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={load} className="text-xs px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1.5" data-testid="analytics-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button type="button" onClick={exportCsv} disabled={!filtered.length} className="text-xs px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5 font-medium" data-testid="analytics-export">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`text-xs px-3 py-1.5 border transition ${tab === t.key ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
            data-testid={`analytics-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Filter across all columns…"
        className="w-full sm:max-w-md text-xs px-2 py-1.5 bg-background border border-border"
        data-testid="analytics-search"
      />

      <div className="border border-border overflow-x-auto max-h-[70vh]">
        <table className="w-full text-[11px]" data-testid="analytics-table">
          <thead className="bg-muted/40 border-b border-border sticky top-0">
            <tr className="text-left">
              {columns.map((c) => (
                <th key={c} className="px-2 py-1.5 text-[10px] uppercase text-muted-foreground tracking-wide whitespace-nowrap">
                  {c.replace(/_/g, " ")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.slice(0, 500).map((r, i) => (
              <tr key={i} className="hover:bg-muted/20">
                {columns.map((c) => (
                  <td key={c} className="px-2 py-1 whitespace-nowrap">
                    {formatCell(r[c])}
                  </td>
                ))}
              </tr>
            ))}
            {filtered.length === 0 && !loading && (
              <tr><td colSpan={columns.length || 1} className="px-3 py-10 text-center text-muted-foreground">No rows.</td></tr>
            )}
          </tbody>
        </table>
        {filtered.length > 500 && (
          <div className="px-3 py-2 text-[10px] text-muted-foreground bg-muted/20 border-t border-border">
            Showing first 500 of {filtered.length} rows. Refine the search to narrow down.
          </div>
        )}
      </div>
    </div>
  );
}

function formatCell(v) {
  if (v === null || v === undefined) return <span className="text-muted-foreground">—</span>;
  if (typeof v === "number") return <span className="font-mono">{v.toLocaleString("en-IN")}</span>;
  if (typeof v === "object") return <span className="text-[10px] text-muted-foreground">[obj]</span>;
  const s = String(v);
  return s.length > 60 ? s.slice(0, 57) + "…" : s;
}
